import React, { useMemo } from "react";
import { Route } from "react-router-dom";
import { shallowEqual, useSelector } from 'react-redux';

import GroupMenu from "src/components/group-menu";

import GroupInsights from './pages/group-insights';
import Main from './pages/main';
import Declines from './pages/declines';
import Exposure from './pages/exposure';
import Customers from './pages/customers';

import HasRole from "src/utils/has-role";
import HasFeature from "src/utils/has-feature";
import RedirectRenderer from "src/components/redirect-renderer";
import { PresentationChartLineIcon, UserGroupIcon, ScaleIcon, LifebuoyIcon } from "@heroicons/react/24/outline";
import GroupContainer from "src/components/group-container";
import domain from 'src/config/domain';

export const getRoutes = (account, features) => [
  { name: 'Dashboard', description: 'Overview of your organisation', path: '/', check: true },
  { name: 'Insights', description: 'Performance metrics for your organisation', path: '/insights', check: true },
  { name: 'Group Insights', description: 'Performance metrics for all child organisations', path: '/insights/group', check: true },
  { name: 'Customer Insights', description: 'An overview of your customers\' performance', path: '/insights/customers', check: HasRole(account, ['booking-and-payment:organisation-access']) },
  { name: 'Payment Acceptance', description: 'An overview of your payment acceptance performance', path: '/insights/payment-acceptance', check: HasRole(account, ['acquirer:reporting']) && HasFeature(features, ['acquirer-reporting']) },
  { name: 'Exposure', description: 'An overview of your exposure to risk', path: '/insights/exposure', check: HasRole(account, ['organisation:risk:statistics']) && HasFeature(features, ['risk']) },
];

const Layout = React.memo(({ children }) => {
  const domainConfig = domain();

  const { organisation: { features, organisations, organisation }, account: { account }} = useSelector(
    (state) => ({
      organisation: {
        features: state.organisation.features,
        organisations: state.organisation.organisations,
        organisation: state.organisation.organisation,
      },
      account: {
        account: state.account.account
      },
    }),
    shallowEqual,
  );

  let orgHasChildren = organisations.filter((org) => org?.parent?.id === organisation).length > 0;

  let items = [];

  if (orgHasChildren === true) {
    items.push({ name: 'Group Insights', description: 'Performance metrics for all child organisations', icon: UserGroupIcon, path: '/insights/group', check: true });
  }

  items.push({ name: `${orgHasChildren === true ? 'Organisation ' : ''}Insights`, description: 'Performance metrics for your organisation', icon: PresentationChartLineIcon, path: '/insights', check: true });
  items.push({ name: 'Customers', description: 'An overview of your customers\' performance', icon: UserGroupIcon, path: '/insights/customers', check: HasRole(account, ['booking-and-payment:organisation-access']) });
  items.push({ name: 'Payment Acceptance', description: 'An overview of your payment acceptance performance (declines, authorisations, etc..)', icon: ScaleIcon, path: '/insights/payment-acceptance', check: HasRole(account, ['acquirer:reporting']) && HasFeature(features, ['acquirer-reporting']) });
  
  if (domainConfig.isAcquirerFlow === false) {
    items.push({ name: 'Exposure', description: 'An overview of your exposure to risk', icon: LifebuoyIcon, path: '/insights/exposure', check: HasRole(account, ['organisation:risk:statistics']) && HasFeature(features, ['risk']) });
  }

  return (
    <div id="content">
      <GroupMenu items={items} title="Insights" />
      <GroupContainer>
        {children}
      </GroupContainer>
    </div>
  )
});

const Dashboard = () => {
  const { organisation: { features, organisations, organisation }, account: { account }} = useSelector(
    (state) => ({
      organisation: {
        features: state.organisation.features,
        organisations: state.organisation.organisations,
        organisation: state.organisation.organisation
      },
      account: {
        account: state.account.account
      },
    }),
    shallowEqual,
  );


  let orgHasChildren = organisations.filter((org) => org?.parent?.id === organisation).length > 0;

  const redirects = useMemo(() => {
    if (orgHasChildren) {
      return [{ from: '/', to: '/insights/group' }];
    }
    return [
      { from: '/', to: '/insights' },
      { from: '/insights/group', to: '/insights' },
    ];
  }, [orgHasChildren]);

  if (account?.id) {
    return (
      <React.Fragment>

        {RedirectRenderer({ redirects })}

        <Route path="/insights" exact component={() => <Layout><Main /></Layout>} />

        {orgHasChildren === true && (
          <Route path="/insights/group" exact component={() => <Layout><GroupInsights /></Layout>} />
        )}

        {HasRole(account, ['booking-and-payment:organisation-access']) && (
          <Route path="/insights/customers" component={() => <Layout><Customers /></Layout>} />
        )}

        {HasRole(account, ['acquirer:reporting']) && HasFeature(features, ['acquirer-reporting']) && (
          <Route path="/insights/payment-acceptance" component={() => <Layout><Declines /></Layout>} />
        )}

        {HasRole(account, ['organisation:risk:statistics']) && HasFeature(features, ['risk']) && (
          <Route exact path={`/insights/exposure`} component={() => <Layout><Exposure /></Layout>} />
        )}
      </React.Fragment>
    );
  }

  return <React.Fragment />;
};

export default Dashboard;
